/** Thin fetch wrapper for the JSON API. Cookies carry the session; nothing is cached here. */

export class ApiError extends Error {
  constructor(
    public status: number,
    public detail: string,
  ) {
    super(detail);
  }
}

async function request<T>(method: string, path: string, body?: unknown, raw?: BodyInit, headers?: Record<string, string>): Promise<T> {
  const init: RequestInit = {
    method,
    credentials: 'same-origin',
    headers: { ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}), ...(headers ?? {}) },
  };
  if (body !== undefined) init.body = JSON.stringify(body);
  if (raw !== undefined) init.body = raw;
  const res = await fetch(path, init);
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const json = await res.json();
      detail = typeof json.detail === 'string' ? json.detail : JSON.stringify(json.detail);
    } catch {
      /* no JSON body */
    }
    throw new ApiError(res.status, detail);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  putRaw: <T>(path: string, raw: BodyInit, contentType: string) =>
    request<T>('PUT', path, undefined, raw, { 'Content-Type': contentType }),
  del: <T = void>(path: string) => request<T>('DELETE', path),
};

// ---------------------------------------------------------------------------- API types

export interface User {
  id: string;
  email: string;
  display_name: string;
  role: 'admin' | 'reader';
  created_at: string;
  disabled: boolean;
}

export interface Me {
  user: User;
  app_version: string;
  research_use: { banner: string; accepted: boolean; accepted_at: string | null };
}

export interface ProjectCard {
  id: string;
  name: string;
  notes: string;
  has_notes: boolean;
  created_at: string;
  last_run_id: string | null;
  last_run_at: string | null;
  run_count: number;
  pipeline_versions: string[];
  thumbnail_url: string | null;
}

/** A document the hub's worker rendered with the job, copied beside the run by ingest. */
export interface RunReport {
  name: string;
  display_name: string | null;
  filename: string;
  bytes: number;
  content_type: string | null;
  /** Presigned GET; null only when the run has no storage prefix. */
  url: string | null;
}

export interface RunSummary {
  id: string;
  project_id: string;
  pipeline_version: string | null;
  primary_frame: number | null;
  num_frames: number;
  is_all_frames: boolean;
  started_at: string | null;
  finished_at: string | null;
  ingested_at: string;
  ingested_by: string | null;
  thumbnail_url: string | null;
  measurements_url: string | null;
  centerline_url: string | null;
  asset_count: number;
  total_bytes: number;
  reports: RunReport[];
}

export interface ProjectDetail extends ProjectCard {
  runs: RunSummary[];
}

// ------------------------------------------------------------- pipeline-hub jobs

/** A report a bundle version can render after the run; asked for by name at submission. */
export interface HubReportInfo {
  name: string;
  display_name: string | null;
  /** Template; `{project_name}` is filled by the hub at submission. */
  filename: string | null;
}

export interface HubPipelineVersion {
  version: string;
  state: 'available' | 'disabled' | 'missing';
  display_name: string | null;
  allowed_client_args: string[];
  reports: HubReportInfo[];
  is_default: boolean;
}

export interface HubPipeline {
  name: string;
  display_name: string;
  default_version: string | null;
  latest_version: string | null;
  workers_online: number;
  workers_eligible: number;
  versions: HubPipelineVersion[];
}

export interface HubWorker {
  name: string;
  online: boolean;
  busy: boolean;
  current_job_id: string | null;
  gpu_name: string | null;
  gpu_memory_mb: number | null;
  installed: string[];
  last_seen_at: string | null;
}

export interface HubInfo {
  configured: boolean;
  url: string | null;
  bucket: string | null;
  default_pipeline: string;
  online: boolean;
  workers_online: number;
  workers: HubWorker[];
  pipelines: HubPipeline[];
  error: string | null;
  /** The backend converts succeeded jobs into runs itself (ingest CLI as a subprocess). */
  auto_ingest: boolean;
  ingest: { web_bucket: string; region: string; api: string; hub_bucket: string | null };
  upload: { max_bytes: number; accept: string[] };
}

export interface UploadTicket {
  upload_id: string;
  key: string;
  filename: string;
  method: 'PUT';
  url: string;
  headers: Record<string, string>;
  expires_in: number;
}

export type JobState = 'queued' | 'preparing' | 'running' | 'succeeded' | 'failed' | 'lost' | 'cancelled';

/** One report requested with a job; `available` once the worker rendered and uploaded it,
 *  `error` when it could not (the job itself still succeeded). */
export interface JobReport {
  name: string;
  display_name: string | null;
  filename: string | null;
  available: boolean;
  error: string | null;
}

export interface Job {
  id: string;
  project_id: string;
  pipeline: string;
  version: string | null;
  state: JobState;
  is_active: boolean;
  percent: number;
  stage: string | null;
  worker_name: string | null;
  pinned_worker: string | null;
  error: string | null;
  exit_code: number | null;
  extra_args: string[];
  input_filename: string;
  input_bytes: number | null;
  result_available: boolean;
  hub_gone: boolean;
  run_id: string | null;
  created_by: string | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  synced_at: string | null;
  ingest_state: 'running' | 'done' | 'failed' | null;
  ingest_error: string | null;
  ingest_started_at: string | null;
  ingest_finished_at: string | null;
  reports: JobReport[];
}

/** Backend → hub relay of an uploaded scan (POST /projects/{id}/jobs answers 202 with one). */
export interface Relay {
  upload_id: string;
  project_id: string;
  filename: string;
  state: 'relaying' | 'done' | 'failed';
  sent: number;
  total: number;
  percent: number;
  error: string | null;
  error_status: number | null;
  job: Job | null;
}

export interface JobLog {
  job_id: string;
  state: JobState;
  since: number;
  next: number;
  lines: string[];
  hub_gone: boolean;
}

export type AssetKind =
  | 'ct'
  | 'full_ct'
  | 'clean_ct'
  | 'overlay'
  | 'field'
  | 'surface'
  | 'prediction'
  | 'tf'
  | 'state'
  | 'source'
  | 'thumbnail';

export interface AssetEntry {
  kind: AssetKind;
  name: string;
  key: string;
  bytes: number;
  content_type: string;
  content_encoding: string | null;
  format: string | null;
  raw_bytes: number | null;
  dims: [number, number, number] | null;
  voxel_size: [number, number, number] | null;
  frame_index: number | null;
  is_primary: boolean;
  frame_label: string | null;
  flags: string[];
  color: [number, number, number, number] | null;
  source_asset_id: string | null;
  source_file: string | null;
  meta: Record<string, unknown>;
  url: string;
}

export interface Manifest {
  schema_version: number;
  run_id: string;
  project_id: string;
  project_name: string;
  pipeline_name: string | null;
  pipeline_version: string | null;
  started_at: string | null;
  finished_at: string | null;
  primary_frame: number | null;
  num_frames: number;
  is_all_frames: boolean;
  frames: { index: number; label: string | null; is_primary: boolean }[];
  volume_dims: [number, number, number] | null;
  voxel_size: [number, number, number] | null;
  assets: AssetEntry[];
  reports?: RunReport[];
  ingested_at: string | null;
  thumbnail_url: string | null;
  url_expires_in: number;
}
